import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import AddSocialLinkModal from '../components/AddSocialLinkModal';
import TikTokEmbed from '../components/TikTokEmbed';

const card = { boxShadow: '0px 2px 8px 0px rgba(0,0,0,0.04)' };

const STATUS_STYLES = {
  pending:   'bg-amber-50 text-amber-700 border border-amber-200',
  approved:  'bg-blue-50 text-blue-600 border border-blue-200',
  monitored: 'bg-green-50 text-green-700 border border-green-200',
  verified:  'bg-purple-50 text-purple-700 border border-purple-200',
  rejected:  'bg-red-50 text-[#c62828] border border-red-200',
  closed:    'bg-gray-100 text-[#8a8a8a] border border-[#e5e5e5]',
};

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

const ProposalDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { profile, user } = useAuth();
  const [proposal, setProposal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSocialLinkModal, setShowSocialLinkModal] = useState(false);

  useEffect(() => { loadProposal(); }, [id]); // eslint-disable-line

  const loadProposal = async () => {
    try {
      setLoading(true);
      const data = await api.get(`/api/proposals/${id}`);
      setProposal(data);
    } catch (error) {
      alert('Error loading proposal: ' + (error.message || 'Unknown error'));
      navigate('/proposals');
    } finally { setLoading(false); }
  };

  const action = async (endpoint, confirmMsg, successMsg) => {
    if (!window.confirm(confirmMsg)) return;
    try {
      await api.patch(`/api/proposals/${id}/${endpoint}`, {});
      loadProposal();
      if (successMsg) alert(successMsg);
    } catch (error) {
      alert('Error: ' + (error.response?.data?.error || error.message));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-[#3fb5d2] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!proposal) return null;

  const status = proposal.display_status || proposal.status;
  const isAdmin = profile?.role === 'admin';
  const isCM    = profile?.role === 'campaign_manager' || isAdmin;
  const isHunter = profile?.role === 'hunter' || profile?.role === 'basic';
  const isOwn   = user?.id && String(proposal.user_id) === String(user.id);
  const canSubmitLink = !proposal.social_media_link && (proposal.status === 'approved' || proposal.status === 'verified') && (isAdmin || (isHunter && isOwn));
  const canEditLink   = isAdmin && proposal.social_media_link && (proposal.status === 'verified' || proposal.status === 'monitored');

  return (
    <div className="space-y-4 w-full min-w-0 max-w-2xl">
      {/* Back nav */}
      <div>
        <button onClick={() => proposal?.campaign_id ? navigate(`/campaigns/${proposal.campaign_id}`) : navigate('/proposals')}
          className="text-[13px] text-[#3fb5d2] hover:text-[#2a798c] font-medium transition-colors mb-2 inline-block">
          ← Kembali ke Campaign Detail
        </button>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-[22px] font-semibold text-[#3b3b3b] leading-8">Detail Proposal</h1>
            <span className={`mt-1 inline-flex items-center px-2.5 py-0.5 rounded-full text-[12px] font-medium ${STATUS_STYLES[status] || 'bg-gray-100 text-[#646464]'}`}>
              {status}
            </span>
          </div>

          {/* Top-level action buttons */}
          <div className="flex items-center gap-2 flex-wrap">
            {isCM && proposal.status === 'pending' && !proposal.display_status && (
              proposal.social_media_link ? (
                <button onClick={() => action('verify', 'Verify proposal ini?')}
                  className="text-[12px] font-semibold px-3 py-1.5 rounded-lg bg-purple-50 text-purple-700 border border-purple-200 hover:bg-purple-100 transition-colors">
                  Verify
                </button>
              ) : (
                <>
                  <button onClick={() => action('approve', 'Approve proposal ini?')}
                    className="text-[12px] font-semibold px-3 py-1.5 rounded-lg bg-green-50 text-green-700 border border-green-200 hover:bg-green-100 transition-colors">
                    Approve
                  </button>
                  <button onClick={() => action('reject', 'Tolak proposal ini?', 'Proposal berhasil ditolak')}
                    className="text-[12px] font-semibold px-3 py-1.5 rounded-lg bg-red-50 text-[#c62828] border border-red-200 hover:bg-red-100 transition-colors">
                    Reject
                  </button>
                </>
              )
            )}
            {isCM && proposal.status === 'approved' && !proposal.display_status && (
              <button onClick={() => action('verify', 'Verify proposal ini?')}
                className="text-[12px] font-semibold px-3 py-1.5 rounded-lg bg-purple-50 text-purple-700 border border-purple-200 hover:bg-purple-100 transition-colors">
                Verify
              </button>
            )}
            {isAdmin && proposal.status === 'verified' && proposal.social_media_link && !proposal.display_status && (
              <>
                <button onClick={() => action('start-monitoring', 'Mulai monitoring proposal ini?')}
                  className="text-[12px] font-semibold px-3 py-1.5 rounded-lg bg-green-50 text-green-700 border border-green-200 hover:bg-green-100 transition-colors">
                  Start Monitoring
                </button>
                <button onClick={() => action('reset-to-pending', 'Reset ke pending?', 'Proposal dikembalikan ke pending')}
                  className="text-[12px] font-semibold px-3 py-1.5 rounded-lg bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 transition-colors">
                  Reset to Pending
                </button>
                <button onClick={() => action('reject', 'Tolak proposal ini?', 'Proposal berhasil ditolak')}
                  className="text-[12px] font-semibold px-3 py-1.5 rounded-lg bg-red-50 text-[#c62828] border border-red-200 hover:bg-red-100 transition-colors">
                  Reject
                </button>
              </>
            )}
            {isAdmin && proposal.status === 'monitored' && proposal.social_media_link && !proposal.display_status && (
              <>
                <button onClick={() => action('reset-to-pending', 'Reset ke pending?', 'Proposal dikembalikan ke pending')}
                  className="text-[12px] font-semibold px-3 py-1.5 rounded-lg bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 transition-colors">
                  Reset to Pending
                </button>
                <button onClick={() => action('reject', 'Tolak proposal ini?', 'Proposal berhasil ditolak')}
                  className="text-[12px] font-semibold px-3 py-1.5 rounded-lg bg-red-50 text-[#c62828] border border-red-200 hover:bg-red-100 transition-colors">
                  Reject
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Main detail card */}
      <div className="bg-white rounded-2xl p-6 space-y-5" style={card}>
        {/* Campaign */}
        <div>
          <p className="text-[11px] font-medium text-[#8a8a8a] uppercase tracking-wider mb-1">Campaign</p>
          <p className="text-[14px] font-semibold text-[#3b3b3b]">{proposal.campaigns?.name || '—'}</p>
          {proposal.campaigns?.description && (
            <p className="text-[12px] text-[#8a8a8a] mt-0.5 line-clamp-2">{proposal.campaigns.description}</p>
          )}
        </div>

        <div className="border-t border-[#e5e5e5]" />

        {/* Submitted by */}
        <div>
          <p className="text-[11px] font-medium text-[#8a8a8a] uppercase tracking-wider mb-1">Diajukan Oleh</p>
          <p className="text-[13px] text-[#3b3b3b]">{proposal.user_profiles?.full_name || proposal.user_profiles?.email || '—'}</p>
        </div>

        {/* Social media link */}
        {proposal.social_media_link ? (
          <div>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3 mb-2">
              <div className="flex items-center gap-2">
                <p className="text-[11px] font-medium text-[#8a8a8a] uppercase tracking-wider">Social Media Link</p>
                {proposal.social_media_platform && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-[#ecf8fb] text-[#2a798c] border border-[#3fb5d2]/30">
                    {proposal.social_media_platform}
                  </span>
                )}
              </div>
              {canEditLink && (
                <button onClick={() => setShowSocialLinkModal(true)}
                  className="text-[12px] font-medium text-[#3fb5d2] hover:text-[#2a798c] transition-colors">Edit Link</button>
              )}
            </div>
            {proposal.submitted_social_media_link && proposal.submitted_social_media_link !== proposal.social_media_link && (
              <p className="text-[11px] text-[#8a8a8a] mb-1">
                Submitted: <a href={proposal.submitted_social_media_link} target="_blank" rel="noopener noreferrer" className="text-[#3fb5d2] hover:text-[#2a798c]">{proposal.submitted_social_media_link}</a>
              </p>
            )}
            <a href={proposal.social_media_link} target="_blank" rel="noopener noreferrer"
              className="text-[13px] text-[#3fb5d2] hover:text-[#2a798c] break-all transition-colors">
              {proposal.social_media_link}
            </a>
            {proposal.social_media_platform === 'Tiktok' && (
              <div className="mt-3"><TikTokEmbed url={proposal.social_media_link} /></div>
            )}
            {proposal.social_media_posted_at && (
              <p className="text-[11px] text-[#8a8a8a] mt-1">Diposting: {fmtDate(proposal.social_media_posted_at)}</p>
            )}
          </div>
        ) : (canSubmitLink ? (
          <div className={`rounded-xl p-4 border ${proposal.status === 'approved' ? 'bg-blue-50 border-blue-200' : 'bg-purple-50 border-purple-200'}`}>
            <p className={`text-[13px] font-medium mb-3 ${proposal.status === 'approved' ? 'text-blue-700' : 'text-purple-700'}`}>
              {proposal.status === 'approved' ? '✓ Proposal disetujui — tambahkan link social media post' : '✓ Proposal terverifikasi — tambahkan link social media post'}
            </p>
            <button onClick={() => setShowSocialLinkModal(true)}
              className="text-[12px] font-semibold bg-[#3fb5d2] hover:bg-[#2a798c] text-white px-4 py-2 rounded-lg transition-colors">
              Submit Social Media Link
            </button>
          </div>
        ) : null)}

        {/* Status info banners */}
        {proposal.status === 'monitored' && !proposal.display_status && (
          <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3">
            <p className="text-[13px] font-medium text-green-700">✓ Proposal sedang dimonitor — statistik akan di-sync</p>
          </div>
        )}
        {proposal.status === 'rejected' && (
          <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3">
            <p className="text-[13px] font-medium text-[#c62828]">✗ Proposal ditolak — tidak akan di-monitor</p>
          </div>
        )}
        {proposal.display_status === 'closed' && (
          <div className="bg-gray-100 border border-[#e5e5e5] rounded-xl px-4 py-3">
            <p className="text-[13px] text-[#8a8a8a]">⚠️ Campaign sudah ditutup — proposal tidak akan di-sync lagi</p>
          </div>
        )}

        <div className="border-t border-[#e5e5e5]" />

        {/* Timestamps */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-[12px]">
          {[
            ['Dibuat', proposal.created_at],
            ['Disetujui', proposal.approved_at],
            ['Diverifikasi', proposal.verified_at],
            ['Ditolak', proposal.rejected_at],
            ['Diperbarui', proposal.updated_at],
          ].filter(([, v]) => v).map(([label, val]) => (
            <div key={label}>
              <span className="text-[#8a8a8a]">{label}: </span>
              <span className="text-[#3b3b3b]">{fmtDate(val)}</span>
            </div>
          ))}
        </div>
      </div>

      {showSocialLinkModal && (
        <AddSocialLinkModal
          proposal={proposal}
          onClose={() => setShowSocialLinkModal(false)}
          onSuccess={() => { loadProposal(); setShowSocialLinkModal(false); }}
        />
      )}
    </div>
  );
};

export default ProposalDetail;
